import React from "react";
import { Helmet } from "react-helmet";
import { navigate } from "gatsby-link";
import Layout from "../../components/Layout";
import Stage from "../../components/Stage/Stage";
import Container from "../../components/share/Container/Container";
import { career } from "../../config/career";
import "./karriere.scss";

function encode(data) {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

// Bewerbungen laufen ueber dasselbe Netlify-Formular wie die Kontaktseite
// ("contact"), damit die bestehenden Benachrichtigungen weiter greifen. Die
// Kategorie wird als verstecktes Feld fest mitgeschickt.
function handleSubmit(e) {
  e.preventDefault();
  const form = e.target;

  const data = {};
  new FormData(form).forEach((value, key) => {
    data[key] = value;
  });

  fetch("/", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: encode(data),
  })
    .then(() => navigate(form.getAttribute("action")))
    .catch((error) => alert(error));
}

const jobPostingSchema = () => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: career.jobPosting.title,
  description: [
    ...career.hero.intro,
    ...career.sections.map(
      (s) => `<h3>${s.title}</h3><ul>${s.items.map((i) => `<li>${i}</li>`).join("")}</ul>`
    ),
    career.note,
  ].join(""),
  datePosted: career.jobPosting.datePosted,
  validThrough: career.jobPosting.validThrough,
  employmentType: career.jobPosting.employmentType,
  hiringOrganization: {
    "@type": "Organization",
    name: "Heinze Pool GmbH",
    sameAs: "https://heinze-pool.de",
    logo: "https://heinze-pool.de/img/hp-logo_drop.png",
  },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Siemensstraße 3",
      addressLocality: "Isernhagen",
      postalCode: "30916",
      addressRegion: "Niedersachsen",
      addressCountry: "DE",
    },
  },
  directApply: true,
});

const CareerPage = ({ location }) => {
  const { hero, facts, sections, note, closing, contact, form, page } = career;

  // Das Angebot an die Bewerber traegt die Seite – es bekommt das grosse
  // dunkle Panel, Aufgaben und Anforderungen bleiben das ruhige Faktenpaar.
  const offer = sections.find((section) => section.highlight);
  const details = sections.filter((section) => !section.highlight);

  return (
    <Layout
      pageTitle={career.metaTitle}
      pageDescription={career.metaDescription}
      pagePath={location && location.pathname}
    >
      {page.enabled && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(jobPostingSchema())}
          </script>
        </Helmet>
      )}

      <div className="CareerPageRoot">
        <div className="CareerPage__stage">
          <Stage
            title={hero.title}
            image={hero.stageImage}
            description={page.enabled ? hero.role : null}
            isStarter={false}
          />
        </div>

        {page.enabled && (
          <section className="CareerFacts" aria-label="Eckdaten der Stelle">
            <Container variant="no-top">
              <ul className="CareerFacts__list">
                {facts.map((fact, i) => (
                  <li key={i} className="CareerFacts__item">
                    <span className="CareerFacts__label">{fact.label}</span>
                    <span className="CareerFacts__value">{fact.value}</span>
                  </li>
                ))}
              </ul>
            </Container>
          </section>
        )}

        <section className="CareerIntro">
          <Container variant="no-top">
            <div className="CareerIntro__grid">
              <div className="CareerIntro__text">
                <span className="CareerIntro__eyebrow">{hero.eyebrow}</span>

                {page.enabled ? (
                  <>
                    <p className="CareerIntro__lead">{hero.lead}</p>
                    {hero.intro.map((paragraph, i) => (
                      <p
                        key={i}
                        className="CareerIntro__paragraph"
                        dangerouslySetInnerHTML={{ __html: paragraph }}
                      />
                    ))}
                    <a className="CareerIntro__cta" href="#bewerbung">
                      {hero.ctaLabel}
                    </a>
                  </>
                ) : (
                  <p className="CareerIntro__paragraph">{page.closedNotice}</p>
                )}
              </div>

              <figure className="CareerIntro__poster">
                <img src={hero.poster} alt={hero.posterAlt} loading="lazy" />
                <figcaption>{hero.posterCaption}</figcaption>
              </figure>
            </div>
          </Container>
        </section>

        {page.enabled && offer && (
          <section className="CareerOffer">
            <Container variant="no-top">
              <h2 className="CareerOffer__title">{offer.title}</h2>
              <ul className="CareerOffer__list">
                {offer.items.map((item, i) => (
                  <li
                    key={i}
                    className="CareerOffer__item"
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                ))}
              </ul>
            </Container>
          </section>
        )}

        {page.enabled && details.length > 0 && (
          <section className="CareerDetails">
            <Container variant="no-top">
              <div className="CareerDetails__grid">
                {details.map((section, i) => (
                  <article key={i} className="CareerDetails__card">
                    <h2 className="CareerDetails__cardTitle">{section.title}</h2>
                    <ul className="CareerDetails__list">
                      {section.items.map((item, j) => (
                        <li
                          key={j}
                          className="CareerDetails__item"
                          dangerouslySetInnerHTML={{ __html: item }}
                        />
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
              {note && (
                <p
                  className="CareerDetails__note"
                  dangerouslySetInnerHTML={{ __html: note }}
                />
              )}
            </Container>
          </section>
        )}

        <section className="CareerApply" id="bewerbung">
          <Container variant="no-top">
            <div className="CareerApply__grid">
              <div className="CareerApply__intro">
                <h2 className="CareerApply__title">{closing.title}</h2>
                {closing.text.map((paragraph, i) => (
                  <p
                    key={i}
                    className="CareerApply__text"
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
                <div className="CareerApply__contacts">
                  <a
                    className="CareerApply__contact"
                    href={`tel:${contact.phoneHref}`}
                  >
                    <span className="CareerApply__contactLabel">Telefon</span>
                    <span className="CareerApply__contactValue">
                      {contact.phone}
                    </span>
                  </a>
                  <a
                    className="CareerApply__contact"
                    href={`mailto:${contact.mail}`}
                  >
                    <span className="CareerApply__contactLabel">E-Mail</span>
                    <span className="CareerApply__contactValue">
                      {contact.mail}
                    </span>
                  </a>
                </div>
                <p className="CareerApply__signature">
                  HeinzePool – Planung | Bau | Service
                </p>
              </div>

              <div className="CareerApply__formPanel">
                <div className="CareerApply__formHeader">
                  <span className="CareerApply__eyebrow">{form.eyebrow}</span>
                  <h2>{form.title}</h2>
                  <p>{form.text}</p>
                </div>

                <form
                  name="contact"
                  method="post"
                  action="/contact/thanks/"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="CareerApply__form"
                >
                  {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
                  <input type="hidden" name="form-name" value="contact" />
                  {/* Kategorie steht auf dieser Seite fest – siehe form.category. */}
                  <input type="hidden" name="anliegen" value={form.category} />
                  <div hidden>
                    <label>
                      Nicht ausfüllen: <input name="bot-field" />
                    </label>
                  </div>

                  <div className="CareerApply__field">
                    <label htmlFor="name">Dein Name</label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required={true}
                      placeholder="Max Mustermann"
                    />
                  </div>

                  <div className="CareerApply__fieldGroup">
                    <div className="CareerApply__field">
                      <label htmlFor="email">E-Mail Adresse</label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        required={true}
                        placeholder="mail@beispiel.de"
                      />
                    </div>
                    <div className="CareerApply__field">
                      <label htmlFor="phone">Telefonnummer</label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        placeholder="0123 4567890"
                      />
                    </div>
                  </div>

                  <div className="CareerApply__field">
                    <label htmlFor="message">Deine Nachricht</label>
                    <textarea
                      name="message"
                      id="message"
                      required={true}
                      rows="6"
                      placeholder="Erzähl uns kurz, was du bisher gemacht hast und ab wann du kannst …"
                    />
                  </div>

                  <div className="CareerApply__submit">
                    <button type="submit" className="CareerApply__button">
                      Bewerbung senden
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Container>
        </section>
      </div>
    </Layout>
  );
};

export default CareerPage;
